import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Box, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BeneficiariesService, { CreateBeneficiaryDTO, BankAccountType } from 'services/beneficiaries.service';
import { useToasts } from 'hooks/toasts';
import { AxiosResponse } from 'axios';
import { AccountService, CarrierService } from 'services';
import { ICarrierBranch, ICarrierRoot } from 'common/carrier.interface';
import BeneficiaryFields from './BeneficiaryModal/BeneficiaryFields';
import AccountOrPixFields from './BeneficiaryModal/AccountOrPixFields';
import DeleteBeneficiaryDialog from './BeneficiaryModal/DeleteBeneficiaryDialog';
import { EBeneficiaryType } from './BeneficiaryModal';

type BeneficiaryTitularModalProps = {
  open: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  initialData?: any;
  onSubmitted?: () => void;
};

enum EAccountTypePortuguese {
  CORRENTE = 'CORRENTE',
  POUPANCA = 'POUPANCA',
  PAGAMENTO = 'PAGAMENTO',
}

const ACCOUNT_TYPE_MAP: Record<EAccountTypePortuguese, BankAccountType> = {
  CORRENTE: 'CHECKING',
  POUPANCA: 'SAVINGS',
  PAGAMENTO: 'PAYMENT',
};

const mapServiceToPortuguese = (t?: string | BankAccountType | null): EAccountTypePortuguese | '' => {
  switch (t) {
    case 'CHECKING':
      return EAccountTypePortuguese.CORRENTE;
    case 'SAVINGS':
      return EAccountTypePortuguese.POUPANCA;
    case 'PAYMENT':
      return EAccountTypePortuguese.PAGAMENTO;
    default:
      return '';
  }
};

const mapEarlyDiscountTypeDisplay = (t: ('FIXED' | 'PERCENTAGE') | null | undefined): string => {
  if (!t) return '';
  return t === 'FIXED' ? 'fixo' : 'percentual';
};

const accountService = new AccountService();
const carrierService = new CarrierService();

const BeneficiaryTitularModal: FC<BeneficiaryTitularModalProps> = ({ open, onClose, mode = 'create', initialData, onSubmitted }) => {
  const service = React.useMemo(() => new BeneficiariesService(), []);
  const toast = useToasts();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [carrierInfo, setCarrierInfo] = React.useState<{ carrierRoot: ICarrierRoot; carrierBranch: ICarrierBranch } | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchCarrerInfo = async () => {
      if (carrierInfo) return;

      const loggedUserInfo = await accountService.getMe();

      if (!loggedUserInfo.carrierRootId || !loggedUserInfo.carrierBranchId) {
        toast.triggerToast('Informações da conta insuficientes para cadastrar beneficiário.', { variant: 'error' });
        onClose();
        return;
      }

      const carrierRoot = await carrierService.getCarrierRoot(loggedUserInfo.carrierRootId);
      const carrierBranch = await carrierService.getCarrierBranch(loggedUserInfo.carrierBranchId);

      setCarrierInfo({ carrierRoot, carrierBranch });
    };

    fetchCarrerInfo();
  }, [open]);

  const validationSchema = Yup.object({
    bankCode: Yup.string().required('Instituição financeira obrigatória'),
    bankBranch: Yup.string().required('Agência obrigatória'),
    bankAccount: Yup.string().required('Conta obrigatória'),
    accountTypePt: Yup.string().required('Tipo de conta obrigatório'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      companyName: carrierInfo?.carrierRoot.companyName || '',
      tradeName: carrierInfo?.carrierRoot.tradeName || '',
      documentNumber: carrierInfo?.carrierBranch.documentNumber || '',
      pixKey: initialData?.pixKey || '',
      bankCode: initialData?.bankCode || '',
      bankBranch: initialData?.branch || '',
      bankAccount:
        initialData?.accountNumber && initialData?.accountDigit ? `${initialData.accountNumber}-${initialData.accountDigit}` : '',
      accountTypePt: mapServiceToPortuguese(initialData?.accountType) || '',
      postalCode: initialData?.postalCode || '',
      street: initialData?.street || '',
      streetNumber: initialData?.streetNumber || '',
      addressComplement: initialData?.addressComplement || '',
      neighborhood: initialData?.neighborhood || '',
      city: initialData?.city || '',
      stateUf: initialData?.state || '',
      lateFinePercent: initialData?.lateFeePercentage ? String(initialData.lateFeePercentage) : '',
      lateMonthlyInterestPercent: initialData?.latePaymentInterestRate ? String(initialData.latePaymentInterestRate) : '',
      lateDays: initialData?.latePaymentGraceDays ? String(initialData.latePaymentGraceDays) : '',
      earlyDiscountType: mapEarlyDiscountTypeDisplay(initialData?.earlyPaymentConditionType),
      earlyDiscountValue: initialData?.earlyDiscountValue ? String(initialData.earlyDiscountValue) : '',
      earlyDiscountDays: initialData?.earlyPaymentDiscountDays ? String(initialData.earlyPaymentDiscountDays) : '',
      category: initialData?.category || '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const unmaskedDocument = values.documentNumber.replace(/\D/g, '');

        const accRaw = values.bankAccount.replace(/\D/g, '');
        const accDigit = accRaw.slice(-1);
        const accNumber = accRaw.slice(0, -1);

        const payload: CreateBeneficiaryDTO = {
          businessName: values.companyName,
          tradingName: values.tradeName,
          beneficiaryType: 'COMPANY',
          counterpartyType: 'FUNDS_TRANSFER',
          taxNumber: unmaskedDocument,
          bankCode: values.bankCode,
          bankBranch: values.bankBranch,
          bankAccount: accNumber,
          bankAccountDigit: accDigit,
          bankAccountType: ACCOUNT_TYPE_MAP[values.accountTypePt as EAccountTypePortuguese],
          category: values.category,
        };

        if (mode === 'edit' && initialData?.id) {
          await service.update(Number(initialData.id), payload);
          toast.triggerToast('Beneficiário atualizado com sucesso.', { variant: 'success' });
        } else {
          await service.create(payload);
          toast.triggerToast('Beneficiário criado com sucesso.', { variant: 'success' });
        }

        resetForm();
        onClose();
        onSubmitted?.();
      } catch (err) {
        const response = err as AxiosResponse;
        const backendMsg = response?.data?.detail || response?.data?.message;

        if (response?.status === 400 && typeof backendMsg === 'string') {
          toast.triggerToast(backendMsg, { variant: 'error' });
        } else {
          toast.triggerToast('Erro ao salvar o beneficiário.', { variant: 'error' });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, isValid, submitForm, resetForm } = formik;

  const canSubmit = isValid && !isSubmitting;

  const handleConfirmDelete = async () => {
    try {
      if (!initialData?.id) return;

      await service.deactivate(Number(initialData.id));

      toast.triggerToast('Beneficiário desativado com sucesso.', { variant: 'success' });

      setDeleteDialogOpen(false);
      onClose();
      onSubmitted?.();
    } catch {
      toast.triggerToast('Erro ao desativar o beneficiário.', { variant: 'error' });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '20px', pt: 1.5, pb: 1 }}>
            Cadastrar conta de mesma titularidade
          </Typography>

          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: '45%', transform: 'translateY(-50%)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box mb={2}>
            <BeneficiaryFields
              values={values}
              touched={touched}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              readOnly
            />
          </Box>

          <Box mb={3}>
            <AccountOrPixFields
              selectedType={EBeneficiaryType.ACCOUNT}
              setSelectedType={() => ({})}
              values={values}
              touched={touched}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              getErrorText={(name: string) => (touched[name] ? errors[name] : undefined)}
              readOnly
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              variant="outlined"
              onClick={() => {
                onClose();
                resetForm();
              }}
              sx={{ borderRadius: 6, flex: 1 }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              disabled={!canSubmit}
              sx={{ borderRadius: 6, flex: 1 }}
              onClick={async () => {
                await submitForm();
                resetForm();
              }}
            >
              {mode === 'edit' ? 'Salvar alterações' : 'Cadastrar'}

              {isSubmitting && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
            </Button>
          </Box>

          {mode === 'edit' && initialData?.id && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineOutlinedIcon />}
                sx={{ borderRadius: 6, width: '20vw' }}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Excluir Contraparte
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      <DeleteBeneficiaryDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} onConfirm={handleConfirmDelete} />
    </>
  );
};

export default BeneficiaryTitularModal;
